// Supervisor Redux Slice

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supervisorApi } from '../../api/supervisorApi';

// ─── Async Thunks ──────────────────────────────────────────────────────────────

export const fetchSupervisors = createAsyncThunk(
  'supervisors/fetchAll',
  // params: { period } or { from, to } — powers the dashboard numbers
  // (inquiries in, quoted, reply %) and each supervisor's share.
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await supervisorApi.getList(params);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to load supervisors');
    }
  }
);

export const fetchDeletedSupervisors = createAsyncThunk(
  'supervisors/fetchDeleted',
  async (_, { rejectWithValue }) => {
    try {
      const res = await supervisorApi.getList({ include_deleted: true });
      const all = res.data.results ?? res.data;
      return all.filter((s) => s.is_deleted);
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to load deleted supervisors');
    }
  }
);

export const createSupervisor = createAsyncThunk(
  'supervisors/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await supervisorApi.create(data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to create supervisor');
    }
  }
);

export const updateSupervisor = createAsyncThunk(
  'supervisors/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await supervisorApi.update(id, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to update supervisor');
    }
  }
);

export const toggleSupervisorActive = createAsyncThunk(
  'supervisors/toggleActive',
  async ({ id, is_active }, { rejectWithValue }) => {
    try {
      const res = await supervisorApi.toggleActive(id, is_active);
      return res.data.supervisor ?? res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update status');
    }
  }
);

export const deleteSupervisor = createAsyncThunk(
  'supervisors/delete',
  async ({ id, password }, { rejectWithValue }) => {
    try {
      await supervisorApi.remove(id, password);
      return id;
    } catch (err) {
      const data = err.response?.data;
      const msg = Array.isArray(data)
        ? data[0]
        : (data?.detail || data?.password || 'Failed to delete supervisor');
      return rejectWithValue(msg);
    }
  }
);

export const restoreSupervisor = createAsyncThunk(
  'supervisors/restore',
  async (id, { rejectWithValue }) => {
    try {
      const res = await supervisorApi.restore(id);
      return { id, supervisor: res.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to restore supervisor');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const supervisorSlice = createSlice({
  name: 'supervisors',
  initialState: {
    list: [],
    deletedList: [],
    count: 0,
    deletedCount: 0,
    windowInquiries: 0,
    windowQuoted: 0,
    windowPending: 0,
    replyPct: 0,
    loading: false,
    loadingDeleted: false,
    deletedFetched: false,
    submitting: false,
    error: null,
  },
  reducers: {
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    // fetchSupervisors
    builder
      .addCase(fetchSupervisors.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSupervisors.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list    = payload.results ?? payload;
        state.count   = payload.count   ?? (payload.length || 0);
        state.windowInquiries = payload.window_inquiries ?? 0;
        state.windowQuoted    = payload.window_quoted ?? 0;
        state.windowPending   = payload.window_pending ?? 0;
        state.replyPct        = payload.reply_pct ?? 0;
      })
      .addCase(fetchSupervisors.rejected,  (state, { payload }) => { state.loading = false; state.error = payload; });

    // fetchDeletedSupervisors
    builder
      .addCase(fetchDeletedSupervisors.pending,   (state) => { state.loadingDeleted = true; state.error = null; })
      .addCase(fetchDeletedSupervisors.fulfilled, (state, { payload }) => {
        state.loadingDeleted = false;
        state.deletedList    = payload;
        state.deletedCount   = payload.length;
        state.deletedFetched = true;
      })
      .addCase(fetchDeletedSupervisors.rejected,  (state, { payload }) => { state.loadingDeleted = false; state.error = payload; });

    // createSupervisor
    builder
      .addCase(createSupervisor.pending,   (state) => { state.submitting = true; state.error = null; })
      .addCase(createSupervisor.fulfilled, (state, { payload }) => {
        state.submitting = false;
        state.list.unshift(payload);
        state.count += 1;
      })
      .addCase(createSupervisor.rejected,  (state, { payload }) => { state.submitting = false; state.error = payload; });

    // updateSupervisor
    builder
      .addCase(updateSupervisor.pending,   (state) => { state.submitting = true; state.error = null; })
      .addCase(updateSupervisor.fulfilled, (state, { payload }) => {
        state.submitting = false;
        const idx = state.list.findIndex((s) => s.id === payload.id);
        if (idx !== -1) state.list[idx] = payload;
      })
      .addCase(updateSupervisor.rejected,  (state, { payload }) => { state.submitting = false; state.error = payload; });

    // toggleSupervisorActive (optimistic)
    builder
      .addCase(toggleSupervisorActive.pending, (state, { meta }) => {
        const idx = state.list.findIndex((s) => s.id === meta.arg.id);
        if (idx !== -1) state.list[idx].is_active = meta.arg.is_active;
      })
      .addCase(toggleSupervisorActive.fulfilled, (state, { payload }) => {
        const idx = state.list.findIndex((s) => s.id === payload.id);
        if (idx !== -1) state.list[idx] = payload;
      })
      .addCase(toggleSupervisorActive.rejected, (state, { payload, meta }) => {
        const idx = state.list.findIndex((s) => s.id === meta.arg.id);
        if (idx !== -1) state.list[idx].is_active = !meta.arg.is_active;
        state.error = payload;
      });

    // deleteSupervisor — soft delete, moves to deletedList
    builder
      .addCase(deleteSupervisor.pending,   (state) => { state.submitting = true; state.error = null; })
      .addCase(deleteSupervisor.fulfilled, (state, { payload: id }) => {
        state.submitting = false;
        const removed = state.list.find((s) => s.id === id);
        state.list  = state.list.filter((s) => s.id !== id);
        state.count = Math.max(0, state.count - 1);
        if (removed && state.deletedFetched) {
          state.deletedList.unshift({ ...removed, is_deleted: true });
          state.deletedCount += 1;
        }
      })
      .addCase(deleteSupervisor.rejected,  (state, { payload }) => { state.submitting = false; state.error = payload; });

    // restoreSupervisor
    builder
      .addCase(restoreSupervisor.pending,   (state) => { state.submitting = true; state.error = null; })
      .addCase(restoreSupervisor.fulfilled, (state, { payload: { id, supervisor } }) => {
        state.submitting  = false;
        state.deletedList = state.deletedList.filter((s) => s.id !== id);
        state.deletedCount = Math.max(0, state.deletedCount - 1);
        state.list.unshift({ ...supervisor, is_active: true });
        state.count += 1;
      })
      .addCase(restoreSupervisor.rejected,  (state, { payload }) => { state.submitting = false; state.error = payload; });
  },
});

export const { clearError } = supervisorSlice.actions;
export default supervisorSlice.reducer;
